import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Wand2, Expand, AlignLeft, Type, Check, X, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
  { key: 'draft', label: 'Draft article', icon: Sparkles, needs: 'topic' },
  { key: 'improve', label: 'Improve writing', icon: Wand2, needs: 'content' },
  { key: 'expand', label: 'Expand', icon: Expand, needs: 'content' },
  { key: 'excerpt', label: 'Write excerpt', icon: AlignLeft, needs: 'content' },
  { key: 'titles', label: 'Suggest titles', icon: Type, needs: 'any' },
];

const AiBlogAssistant = ({ title, content, category, onApplyContent, onApplyExcerpt, onApplyTitle }) => {
  const [running, setRunning] = useState(null);
  const [output, setOutput] = useState('');
  const [controller, setController] = useState(null);
  const [lastActionKey, setLastActionKey] = useState(null);

  const run = async (action) => {
    setRunning(action);
    setOutput('');
    const ctrl = new AbortController();
    setController(ctrl);
    let acc = '';
    try {
      const res = await fetch(`${API}/admin/ai/blog-assist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
        },
        body: JSON.stringify({ action, topic: title, content, category }),
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'AI request failed');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const evt = JSON.parse(line.slice(6));
          if (evt.error) throw new Error(evt.error);
          if (evt.text) {
            acc += evt.text;
            setOutput(acc);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(e.message || 'AI generation failed');
    } finally {
      setRunning(null);
      setController(null);
    }
  };

  const finished = running === null && output;
  const start = (key) => { setLastActionKey(key); run(key); };

  const apply = () => {
    if (lastActionKey === 'excerpt') onApplyExcerpt(output.trim());
    else if (lastActionKey === 'titles') return;
    else onApplyContent(output.trim());
    toast.success('Applied');
    setOutput('');
  };

  const isHtml = lastActionKey && !['excerpt', 'titles'].includes(lastActionKey);

  return (
    <div className="border border-[#00D2D3]/40 bg-[#00D2D3]/5 p-4 space-y-3" data-testid="ai-blog-assistant">
      <div className="flex items-center gap-2 text-primary font-semibold text-sm">
        <Sparkles size={16} className="text-[#00b5b6]" /> AI Writing Assistant
        <span className="text-xs font-normal text-muted-foreground">· Claude Sonnet 4.6</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {ACTIONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => start(key)}
            disabled={!!running}
            data-testid={`ai-blog-${key}`}
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-primary hover:text-primary px-3 py-1.5 text-xs font-medium disabled:opacity-50 transition-colors"
          >
            <Icon size={13} /> {label}
          </button>
        ))}
        {running && (
          <button
            type="button"
            onClick={() => controller?.abort()}
            data-testid="ai-blog-stop"
            className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 px-3 py-1.5 text-xs font-medium"
          >
            <Square size={12} /> Stop
          </button>
        )}
      </div>

      {(running || output) && (
        <div className="bg-white border border-slate-200" data-testid="ai-blog-output-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
            <span className="text-xs text-muted-foreground">
              {running ? 'Generating…' : lastActionKey === 'titles' ? 'Click a title to use it' : 'Preview — review before applying'}
            </span>
            {finished && (
              <div className="flex gap-2">
                {lastActionKey !== 'titles' && (
                  <button type="button" onClick={apply} data-testid="ai-blog-apply" className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-primary px-3 py-1 hover:bg-primary/90">
                    <Check size={12} /> {lastActionKey === 'excerpt' ? 'Use as excerpt' : 'Replace content'}
                  </button>
                )}
                <button type="button" onClick={() => setOutput('')} data-testid="ai-blog-discard" className="inline-flex items-center gap-1 text-xs text-slate-600 px-2 py-1 hover:bg-slate-100">
                  <X size={12} /> Discard
                </button>
              </div>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto p-4 text-sm" data-testid="ai-blog-output">
            {lastActionKey === 'titles' && finished ? (
              <ul className="space-y-1">
                {output.split('\n').map((t) => t.replace(/^[-*\d.\s]+/, '').trim()).filter(Boolean).map((t, i) => (
                  <li key={i}>
                    <button type="button" onClick={() => { onApplyTitle(t); toast.success('Title applied'); setOutput(''); }} data-testid={`ai-blog-title-${i}`} className="text-left w-full px-3 py-2 hover:bg-[#00D2D3]/10 border border-transparent hover:border-[#00D2D3]/40 transition-colors">
                      {t}
                    </button>
                  </li>
                ))}
              </ul>
            ) : isHtml ? (
              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: output }} />
            ) : (
              <p className="whitespace-pre-wrap">{output}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AiBlogAssistant;
